import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, map } from 'rxjs';
import { limit, where } from 'firebase/firestore';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { UserService } from 'src/app/services/user/user.service';

/**
 * The agent-approvals kinds that are a card waiting on a person (functions human-waits.ts waitCardKind, plus the
 * work pass's note cards, which are kind 'gate'): tool approvals, plans, questions (ask_question, the ones Crewdle
 * builds from a reply, blocker cards), team-hire requests, hand-offs, hire proposals and sign-in requests.
 */
export const PENDING_CARD_KINDS = ['gate', 'plan', 'question', 'team-hire', 'handoff', 'hire-proposal', 'auth-provider'] as const;
export type PendingCardKind = (typeof PENDING_CARD_KINDS)[number];

/** One copy of a card: the message it lives in. */
export interface PendingCardCopy {
  conversationId: string;
  messageId: string;
}

/** One card a human still has to answer (an approval, a question, a plan… in a conversation). */
export interface PendingApproval {
  id: string;
  kind: PendingCardKind;
  agentId: string;
  agentName: string;
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'critical';
  /** 'deferred' = the agent's run has moved on; approving runs the recorded call. */
  status: 'pending' | 'deferred';
  conversationId: string;
  /** Every copy of the card, primary first (`cards[]`: a team-work card lives in the space AND the agent's 1:1).
   *  One record is one card, however many copies — it counts once. */
  copies: PendingCardCopy[];
  /** 'channel' = team space (opens under /messaging), else an agent chat (/agents). Stamped on gate and plan
   *  records only; NeedsYou routes the rest by the user's team and space lists. */
  conversationCategory: string | null;
  requestedAtMs: number;
  /** Org the requesting agent belongs to; null only on records written before it was stamped. */
  organizationId: string | null;
  /** The record's owner (the agent's owner; a sign-in request's project owner), null when not stamped. */
  ownerUid: string | null;
}

/**
 * The user's pending approvals across every conversation they take part in
 * (2026-09-16). Feeds the dashboard "Waiting for your approval" list and the
 * rail badge, so a request that outlived the gateway's window is not lost at
 * the bottom of a chat nobody has open. Reads `agent-approvals` by the
 * participants stamped on each record; the decision itself stays on the card.
 *
 * Scoped to the organization on screen (2026-09-18): the listener is per user
 * and a user's agents span organizations, so without this filter an approval
 * raised by an agent in one org sat on the dashboard of every other org the
 * user switched to ("approvals from another account"). Records without an
 * organizationId (written before it was stamped) stay visible everywhere.
 *
 * Board truth (audit 2026-11-02): the query used to read `userIds
 * array-contains uid, limit(200)` with no status filter — records are never
 * deleted, so once a user had 200 decided records the badge counted an
 * arbitrary sample of them. It now asks for open records only
 * (`status in ['pending','deferred']`, composite index userIds+status in
 * backend/firebase firestore.indexes.json). And the listener is re-opened on
 * every region re-bind: a listener stays on the project it was opened on, so
 * after switching to an org of another region the badge kept the old
 * region's records.
 *
 * Every card waiting on the person (Vincent, 2026-11-03: "Yes, count them"):
 * question and team-hire records now carry `userIds` too (crewdle-sdk-firebase
 * feature/cards-need-you-badge + its backfill), and every person-facing kind is
 * listed — not just gates and plans. The kind is filtered here, client-side, so
 * the userIds+status index still serves the query. A sign-in request is decided
 * by the project owner or an org admin only (Build configureAuthProvider): it
 * counts for them alone, as owner-only cards do in a conversation's count
 * (#1705). NeedsYouService merges this list with the tasks waiting on the
 * person into the one "needs you" count.
 */
@Injectable({ providedIn: 'root' })
export class ApprovalsInboxService implements OnDestroy {
  public readonly pending$ = new BehaviorSubject<PendingApproval[] | null>(null);
  public readonly count$ = this.pending$.pipe(map((items) => items?.length ?? 0));

  private unsubscribe: (() => void) | null = null;
  private orgSub: Subscription | null = null;
  private regionSub: Subscription | null = null;
  private boundUid: string | null = null;
  /** Every pending card of the user, all organizations — filtered in publish(). */
  private all: PendingApproval[] | null = null;

  constructor(
    private firebase: FirebaseService,
    private user: UserService,
    private organization: OrganizationService,
  ) {}

  /** A sign-in request waits on the project owner and org admins only (the callable refuses everyone else). */
  private waitsOnViewer(item: PendingApproval): boolean {
    if (item.kind !== 'auth-provider') return true;
    return item.ownerUid === this.user.user?.uid || this.organization.isAdmin === true;
  }

  /** Idempotent per signed-in user; safe to call from several surfaces. */
  public start(): void {
    const uid = this.user.user?.uid;
    if (!uid || this.boundUid === uid) return;
    // Partial FirebaseService doubles (specs of the surfaces that start this
    // service) have no listener API — the inbox is optional chrome, never a
    // reason for the rail or the dashboard to fail.
    if (typeof this.firebase.firestore?.collectionListen !== 'function') return;
    this.boundUid = uid;
    // Re-publish on an in-place org switch (OrgSwitcherService); partial
    // OrganizationService doubles in specs may not expose orgId$.
    this.orgSub ??= this.organization.orgId$?.subscribe?.(() => this.publish()) ?? null;
    this.listen(uid);
    // Re-listen on the new region's project after a re-bind (region$ replays the current region: skip that one).
    let first = true;
    this.regionSub ??= this.firebase.region$?.subscribe?.(() => {
      if (first) { first = false; return; }
      if (this.boundUid) this.listen(this.boundUid);
    }) ?? null;
  }

  /**
   * (Re)open the listener on the bound region's Firestore. `legacyQuery`: the
   * unfiltered query, used only while the region's userIds+status index is not
   * built yet (failed-precondition) so the inbox never goes dark during a deploy.
   */
  private listen(uid: string, legacyQuery = false): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    // Until the new region answers, nothing is known: no stale badge from the region left behind.
    if (this.all !== null) {
      this.all = null;
      this.pending$.next(null);
    }
    const constraints = legacyQuery
      ? [where('userIds', 'array-contains', uid), limit(200)]
      : [where('userIds', 'array-contains', uid), where('status', 'in', ['pending', 'deferred']), limit(200)];
    this.unsubscribe = this.firebase.firestore.collectionListen(
      'agent-approvals',
      constraints,
      (snapshot) => {
        const now = Date.now();
        this.all = snapshot.docs
          .map((doc) => this.mapApproval(doc.id, doc.data() as Record<string, unknown>, now))
          .filter((item): item is PendingApproval => item !== null)
          .sort((a, b) => b.requestedAtMs - a.requestedAtMs);
        this.publish();
      },
      (error) => {
        if (!legacyQuery && error?.code === 'failed-precondition' && this.boundUid === uid) this.listen(uid, true);
      },
    );
  }

  public get count(): number {
    return this.pending$.value?.length ?? 0;
  }

  /** The current org's slice of the cards waiting on the user (all of them while no org is loaded). */
  private publish(): void {
    if (this.all === null) return;
    const orgId = this.organization.orgId$?.value ?? this.organization.data?.id ?? null;
    const inOrg = orgId ? this.all.filter((a) => a.organizationId === null || a.organizationId === orgId) : this.all;
    this.pending$.next(inOrg.filter((a) => this.waitsOnViewer(a)));
  }

  private mapApproval(id: string, data: Record<string, unknown>, now: number): PendingApproval | null {
    const status = data.status as string | undefined;
    // Gate records written before `kind` existed are gates (the backend reads them the same way).
    const kind = (typeof data.kind === 'string' && data.kind ? data.kind : 'gate') as PendingCardKind;
    if (!(PENDING_CARD_KINDS as readonly string[]).includes(kind)) return null;
    if (status !== 'pending' && status !== 'deferred') return null;
    const expiresAtMs = typeof data.expiresAtMs === 'number' ? (data.expiresAtMs as number) : null;
    // A 'pending' record past the gateway's window is waiting on the human
    // (the backend's flip to 'deferred' may lag) — same as the card.
    const effective: PendingApproval['status'] = status === 'deferred' || (expiresAtMs !== null && now > expiresAtMs) ? 'deferred' : 'pending';
    const conversationId = String(data.conversationId ?? '');
    const messageId = typeof data.messageId === 'string' ? data.messageId : '';
    const listed = (Array.isArray(data.cards) ? (data.cards as Array<{ conversationId?: unknown; messageId?: unknown } | null>) : [])
      .filter((c): c is PendingCardCopy => typeof c?.conversationId === 'string' && !!c.conversationId && typeof c?.messageId === 'string' && !!c.messageId)
      .map((c) => ({ conversationId: c.conversationId, messageId: c.messageId }));
    const copies = listed.length ? listed : (conversationId && messageId ? [{ conversationId, messageId }] : []);
    return {
      id,
      kind,
      ownerUid: typeof data.ownerUid === 'string' && data.ownerUid ? data.ownerUid : null,
      agentId: String(data.agentId ?? ''),
      agentName: String(data.agentName ?? 'Agent'),
      title: String(data.title ?? data.toolName ?? 'Approval required'),
      description: String(data.description ?? ''),
      severity: (data.severity as PendingApproval['severity']) ?? 'warning',
      status: effective,
      conversationId,
      copies,
      conversationCategory: typeof data.conversationCategory === 'string' ? (data.conversationCategory as string) : null,
      requestedAtMs: typeof data.requestedAtMs === 'number' ? (data.requestedAtMs as number) : now,
      organizationId: typeof data.organizationId === 'string' && data.organizationId ? (data.organizationId as string) : null,
    };
  }

  ngOnDestroy(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.orgSub?.unsubscribe();
    this.orgSub = null;
    this.regionSub?.unsubscribe();
    this.regionSub = null;
  }
}
